import { Warning, ArrowUpward, Warehouse } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { demandSourceTooltip } from './demandSource'
import { formatNumber, formatDOS } from '../../utils/format'
import { getDOSStatus } from '../../utils/dos'
import { status as statusTokens } from '../../theme/tokens'

export function DOSCellRenderer({ value, data, colDef }) {
  const monthKey = colDef.field.replace('_dos', '')
  const fcst = data[`${monthKey}_fcst`]
  const dosMin = data.dosMin
  const dosMax = data.dosMax

  if (value == null) return <span style={{ color: '#758075' }}>—</span>

  if (value < 0) {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: statusTokens.critical.bg,
        color: statusTokens.critical.color,
        fontWeight: 700,
        padding: '0 6px',
        borderRadius: 4,
        height: 24,
        fontSize: '0.75rem',
      }}>
        NEG
      </span>
    )
  }

  if (!fcst || fcst === 0) {
    return (
      <span style={{
        color: '#758075',
        fontSize: '0.75rem',
        fontStyle: 'italic',
      }}>
        999+
      </span>
    )
  }

  const dosStatus = getDOSStatus(value, dosMin, dosMax)

  if (dosStatus === 'critical') {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: statusTokens.critical.bg,
        color: statusTokens.critical.color,
        fontWeight: 600,
        padding: '0 6px',
        borderRadius: 4,
        height: 24,
        fontSize: '0.75rem',
      }}>
        <Warning sx={{ fontSize: 14 }} />
        {formatDOS(value)}
      </span>
    )
  }

  if (dosStatus === 'aboveMax') {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: statusTokens.aboveMax.bg,
        color: statusTokens.aboveMax.color,
        fontWeight: 600,
        padding: '0 6px',
        borderRadius: 4,
        height: 24,
        fontSize: '0.75rem',
      }}>
        <ArrowUpward sx={{ fontSize: 14 }} />
        {formatDOS(value)}
      </span>
    )
  }

  return (
    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#191C19' }}>
      {formatDOS(value)}
    </span>
  )
}

export function CasesCellRenderer({ value, data, colDef }) {
  const monthKey = colDef.field.replace('_cases', '')
  const isScenarioModified = data[`${monthKey}_scenarioModified`]
  const isOverride = data[`${monthKey}_override`]

  let borderLeft = 'none'
  let position = 'relative'

  if (isOverride) {
    borderLeft = '3px solid #1A9E2A'
  } else if (isScenarioModified) {
    borderLeft = '3px solid #5F8740'
  }

  return (
    <div style={{
      borderLeft,
      paddingLeft: borderLeft !== 'none' ? 4 : 0,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      position,
      fontSize: '0.8125rem',
    }}>
      {formatNumber(value)}
      {isOverride && (
        <span style={{
          position: 'absolute',
          top: 2,
          right: 2,
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: '#1A9E2A',
        }} />
      )}
    </div>
  )
}

// FAR-67: an item whose demand includes other branch warehouses, or that
// another plant also makes. Shown next to the item, because it explains that
// item's demand -- not an issue with it, so it is deliberately not the warning
// icon.
export function DemandSourceIcon({ data, makingPlant, warehouseNames }) {
  const lines = demandSourceTooltip(data, makingPlant, warehouseNames)
  if (lines.length === 0) return null
  return (
    <Tooltip
      title={(
        <span style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</span>
      )}
    >
      <Warehouse
        aria-label="Demand includes other warehouses"
        sx={{ fontSize: 15, color: '#64748B', cursor: 'pointer', verticalAlign: 'text-bottom', ml: 0.5 }}
      />
    </Tooltip>
  )
}

// The Description cell: the text, plus FAR-67's indicator where it applies.
// makingPlant and warehouseNames come from the grid's context, since a cell
// renderer gets no props of its own.
export function DescriptionCellRenderer({ value, data, context }) {
  return (
    <span>
      {value || ''}
      <DemandSourceIcon
        data={data}
        makingPlant={context?.makingPlant}
        warehouseNames={context?.warehouseNames}
      />
    </span>
  )
}

export function IssueCellRenderer({ value, data }) {
  // FAR-71: made-to-order items are not planned; say so instead of a blank.
  if (data?.isMadeToOrder) {
    return (
      <Tooltip title="Made to order — not planned. No DOS targets, so production and inventory are not projected.">
        <span
          aria-label="Made to order — not planned"
          style={{
            fontSize: '0.625rem',
            fontWeight: 700,
            color: '#475569',
            backgroundColor: '#E2E8F0',
            borderRadius: 4,
            padding: '1px 4px',
            lineHeight: '16px',
          }}
        >
          MTO
        </span>
      </Tooltip>
    )
  }
  if (!value) return null
  return (
    <Warning sx={{
      fontSize: 16,
      color: '#B45309',
      cursor: 'pointer',
    }} />
  )
}
